import { TimePassed } from "../../TimePassed";
import { OrderDetails } from "./OrderDetails";
import orderNotificationAudio from "../../audio/order-notification-sound.mp3";
import { SoundNotification } from "../../sound-notification/SoundNotification";
import { DashboardOrderColumn, OrderContainer } from "../../OrderDesign";
import { SendOrderToKitchen } from "./SendOrderToKitchen";
import styled from "styled-components";
import { EditOrder } from "./EditOrder";
import { Box, Chip, Icon, Paper, Typography } from "@mui/material";
import { CompleteOrderButton } from "./CompleteOrderButton";
import { useOrderDataManager } from "./orderDataManager";
import { OrderMarkReady } from "./OrderMarkReady";
import { Paid, ReceiptLong } from "@mui/icons-material";
import { OrderType } from "../../../types/OrderType";
import React, { useEffect, useRef } from "react";
import { useItemInventory } from "./ItemInventoryProvider";
import { orderTotalWithTaxAndDiscount } from "./orderTotalUtils";
import { useMenuData } from "../../../menu-provider/MenuDataProvider";

export const ConfirmAlertArgument = {
  title: "Mark as completed?",
  showCancelButton: true,
  confirmButtonColor: "rgba(75, 181, 67, 0.72)",
  cancelButtonColor: "rgba(178, 38, 38, 0.72)",
  confirmButtonText: "YES",
  cancelButtonText: "NO",
};

const OrderHeader = styled.div`
  background-color: #f0f4f8;
  border-bottom: 1px solid #e0e0e0;
  padding: 8px;
  flex-shrink: 0;
`;

export const Orders = () => {
  const { orders, orderStatus } = useOrderDataManager();
  const { reloadInventory } = useItemInventory();
  const isFirstFetch = useRef<boolean>(true);

  useEffect(() => {
    if (orders === undefined) return;
    if (isFirstFetch.current) {
      isFirstFetch.current = false;
      return;
    }
    reloadInventory();
  }, [reloadInventory, orders]);

  return (
    <>
      <OrderContainer height={"calc(100vh - 160px);"}>
        {(orders == undefined || orders.length == 0) && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            width="100%"
            gap={2}
          >
            <ReceiptLong sx={{ fontSize: 72, color: "text.disabled" }} />
            <Typography variant="h5" color="text.secondary" fontWeight={600}>
              No orders yet
            </Typography>
            <Typography variant="body2" color="text.disabled">
              New orders will appear here automatically
            </Typography>
          </Box>
        )}

        {orders?.map((order) => (
          <React.Fragment key={order.id}>
            <Order order={order} />
          </React.Fragment>
        ))}
      </OrderContainer>

      <SoundNotification
        data={orders}
        status={orderStatus}
        soundFile={orderNotificationAudio}
      />
    </>
  );
};

function Order({ order }: { order: OrderType }) {
  const { currency } = useMenuData();

  return (
    <Paper sx={{ marginRight: 3 }} elevation={2}>
      <DashboardOrderColumn>
        <OrderHeader>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={0.75}>
              <Typography fontSize={"16px"} fontWeight={500}>
                {order.tableName}
              </Typography>
              <Chip
                label={`#${order.orderNumber}`}
                size="small"
                sx={{ height: 18, fontSize: "11px", borderRadius: "4px" }}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={0.75}>
              {order.customerName && (
                <Typography fontSize={"13px"} color="#555">
                  {order.customerName}
                </Typography>
              )}
              <TimePassed dateTimeISO={order.createdAt} />
            </Box>
          </Box>

          <Box display="flex" alignItems="center" mt={0.75} gap={0.5}>
            <OrderMarkReady orderId={order.id} isReady={!!order.isReady} />
            <SendOrderToKitchen
              orderId={order.id}
              isSentToKitchen={order.isSentToKitchen}
            />
            <CompleteOrderButton order={order} />
          </Box>
        </OrderHeader>

        <Box flex={1} overflow="hidden">
          <OrderDetails orderDetails={order.orderDetails} />
        </Box>

        <OrderFooter>
          <Box display="flex" alignItems="center" gap={0.5}>
            {order.isPaid === true && (
              <Icon data-testid={"test-id-order-paid-sign"} fontSize="large">
                <Paid fontSize="large" color="success" />
              </Icon>
            )}
          </Box>

          <Typography fontWeight={600} fontSize="14px" color="text.secondary">
            {currency}
            {orderTotalWithTaxAndDiscount(order)}
          </Typography>

          <EditOrder order={order} />
        </OrderFooter>
      </DashboardOrderColumn>
    </Paper>
  );
}

const OrderFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  border-top: 1px solid #e0e0e0;
  background-color: #fafafa;
  flex-shrink: 0;
`;
